import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IStorage } from '@app/models/storage.interface';
import { first, map, take, tap } from 'rxjs/operators';
import { Router } from "@angular/router"
import { environment } from '@app/../environments/environment';
import { State, select$ } from '@app/shared/helpers/store';
import { AreasService } from '@app/services/areas/areas.service';
import { CategoriesService } from '@app/services/categories/categories.service'
import { Store } from '@ngrx/store';
import { selectStoragesState } from '@app/modules/storages/store/selectors/storages.selectors';
import * as StorageActions from '@app/modules/storages/store/actions/storages.actions';
import { selectCurrentItemState } from '@app/modules/organization/store/selectors/organization.selectors';

@Injectable({
  providedIn: 'root'
})

export class StoragesService {
  public endpoint: string = `${environment.apiUrl}/storages`;
  public totalData$: State<number>;
  public pageSizeData$: State<any>;
  public pageSize$: Observable<number>;
  public total$: Observable<any>;
  public currentPageData$: State<number>;
  public currentPage$: Observable<number>;
  public isAllowedToLeaveData$: State<boolean>;
  public isAllowedToLeave$: Observable<any>;
  public isPrdCodeValidData$: State<any>;
  public isPrdCodeValid$: Observable<any>;
  public currentPage;

  constructor(
    private _http: HttpClient,
    private store$: Store
  ) {
    this.totalData$ = new State(0);
    this.pageSizeData$ = new State(100);
    this.total$ = select$(this.totalData$, data => data);
    this.pageSize$ = select$(this.pageSizeData$, data => data);
    this.isAllowedToLeaveData$ = new State(true);
    this.isAllowedToLeave$ = select$(this.isAllowedToLeaveData$, data => data);
  }

  getStorages(sortType, direction, query?, currentPage?) {
    this.store$.select(selectStoragesState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage
      }
    });
  }

  getStorage(id: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/${id}`)
  }

  getStoragesPath(id: number, locationId:number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/storages-path/${id}?locationId=${locationId}`)
  }

  onSave(storage: IStorage): Observable<any> {
    return this._http.post<any>(this.endpoint, storage);
  }

  setPageSize(size) {
    this.pageSizeData$.next(size);
  }

  updateStorage(storage) {
    return this._http.put<any>(`${this.endpoint}/${storage.storageStructureID}`, storage)
  }

  onDelete(id: number) {
    return this._http.delete(`${this.endpoint}/${id}`);
  }

  onClone(id: number,storage:any): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/clone/${id}`,{
      params : storage
    });
  }


  searchStorages(sort, direction, query: string): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/search`, {
      params: {
        query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }

  analyzeStorageImport(payload: { fTypeID: number; locID: number; rows: any[] }): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/import/analyze`, payload);
  }

  confirmStorageImport(payload: { fTypeID: number; locID: number; payload: any[] }): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/import/confirm`, payload);
  }

  exportStorageBulk(fTypeID: number, locID: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/export-bulk`, {
      params: { fTypeID: fTypeID.toString(), locID: locID.toString() }
    });
  }

  // NOTE: kept only for backwards compatibility with the legacy
  // excel-import.component under modules/storages/components/excel-import.
  // That component is no longer wired into any dialog flow but its module
  // declaration must still compile. None of these endpoints exist on the
  // backend.
  importStorages(storages: {}[]) {
    return this._http.post<any>(`${this.endpoint}/import`, { storages });
  }

  confirmImport(storages: {}[]): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/import/confirm`, { storages });
  }

  confirmImportSkusBarcodes(skusBarcodes: {}[]): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/import/confirmSkusBarcodes`, { skusBarcodes });
  }


  onSortStorages(type: string, category?) {
    return this._http.get<any>(`${this.endpoint}/sort/${type}`, {
      params: {
        category: category
      }
    }).pipe(first()).subscribe((data) => {
      this.totalData$.next(data.total);
    });
  }

  compare(a: any | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  batchDelete(selected): Observable<any> {
    return this._http.post(`${this.endpoint}/batchDelete`, { ids: selected });
  }

  onSetIsAllowedToLeave(res) {
    return this.isAllowedToLeaveData$.next(res);
  }

  onGenerateExcel(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/storages/export`);
  }

  onSort(arr, direction) {
    let type = 'name';

    if (arr.length <= 1) {
      return arr;
    }

    var pivot = arr[0];

    var left = [];
    var right = [];

    for (var i = 1; i < arr.length; i++) {
      if (direction == 'desc') {
        arr[i][type] < pivot[type] ? right.push(arr[i]) : left.push(arr[i]);
      } else {
        arr[i][type] < pivot[type] ? left.push(arr[i]) : right.push(arr[i]);
      }

    }

    return this.onSort(left, direction).concat(pivot, this.onSort(right, direction));
  }

  onPaginate(page) {
    this.currentPageData$.next(page);
  }

  getTopItems(sortType, direction, query?, currentPage?,locId?) {
    var selectedLocation;
    this.store$.subscribe((state: any) => {
      if(state!=null && state.core!=null && state.core.me!=null && state.core.me.locID!=null)
      selectedLocation = state.core.me.locID;
    });

    this.store$.select(selectStoragesState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/top?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage,
        locID:selectedLocation
      }
    });
  }


  getAllItems(sortType, direction, query?, currentPage?,locId?) {
    var locId;
    if(locId==0){   
      this.store$.subscribe((state: any) => {
        if(state!=null && state.core!=null && state.core.me!=null && state.core.me.locID!=null){
          locId = state.core.me.locID;
        }
      });
    }

    this.store$.select(selectStoragesState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/all?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage,
        storageLocID:locId
      }
    });
  }

  getChildren(StorageStructure: number,sortType, direction, query?, currentPage?,locId?) {
    var selectedLocation;
    this.store$.subscribe((state: any) => {
      selectedLocation = state.core.me.locID;
    });

    return this._http.get<any>(`${this.endpoint}/children/${StorageStructure}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage,
        locID:selectedLocation
      }
    });
  }

  getItems(sortType, direction, query?, currentPage?, storageStructureID?) {
    this.store$.select(selectStoragesState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/get-items/${storageStructureID}?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage
      }
    });
  }
  getProducts(sortType, direction, query?, currentPage?, storageStructureID?) {
    this.store$.select(selectStoragesState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/get-products/${storageStructureID}?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage
      }
    });
  }


  updateStorageSortOrder(updatedList: any): Observable<any> {
    return this._http.post(`${this.endpoint}/product-storage-order`, updatedList);
  }

  updateStoragePath(storageStructureID) {
    return this._http.get<any>(`${this.endpoint}/update-path/${storageStructureID}`)
  }
  
}


